/**
 * Public read-only dashboard view — the page a share link opens.
 *
 * No auth: authorization IS the unguessable token in the query string,
 * resolved by /api/dashboards/shared. Renders outside AuthGuard and the
 * app shell (App.tsx branches to this page before mounting the guard).
 */

import { useEffect, useState } from 'react';
import {
  Badge,
  Banner,
  BlockStack,
  Box,
  Card,
  InlineGrid,
  InlineStack,
  Page,
  SkeletonBodyText,
  SkeletonPage,
  Text,
} from '@shopify/polaris';

import type { MetricResult, WidgetData } from '../types/marketingSuite';
import {
  BarChart,
  KpiValue,
  LineChart,
} from '../components/marketingSuite/MiniChart';
import { MetricTable } from '../components/marketingSuite/MetricTable';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

interface SharedWidget {
  id: string;
  widget_type: string;
  title: string;
  data?: WidgetData | null;
}

// A widget whose query failed arrives as {error, message}, not a
// MetricResult — and this page renders OUTSIDE the app's error boundary,
// so treating it as a result would white-screen the whole share link.
function liveResult(data: SharedWidget['data']): MetricResult | null {
  if (!data || 'error' in data) return null;
  return data;
}

interface SharedPayload {
  name: string;
  description?: string | null;
  widgets: SharedWidget[];
}

export default function SharedDashboard() {
  const [payload, setPayload] = useState<SharedPayload | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Capability-URL content: keep crawlers out while the page is up.
    const robots = document.createElement('meta');
    robots.name = 'robots';
    robots.content = 'noindex, nofollow';
    document.head.appendChild(robots);
    return () => {
      document.head.removeChild(robots);
    };
  }, []);

  useEffect(() => {
    const token = new URLSearchParams(window.location.search).get('token');
    if (!token) {
      setError('This share link is missing its token.');
      return;
    }
    let cancelled = false;
    fetch(
      `${API_BASE_URL}/api/dashboards/shared?token=${encodeURIComponent(token)}`
    )
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(
            response.status === 404
              ? 'This share link is invalid or has been revoked.'
              : `Could not load the dashboard (${response.status}).`
          );
        }
        return response.json() as Promise<SharedPayload>;
      })
      .then((data) => {
        if (!cancelled) setPayload(data);
      })
      .catch((e) => {
        if (!cancelled) {
          setError(
            e instanceof Error ? e.message : 'Could not load dashboard'
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error) {
    return (
      <Page title="Shared dashboard">
        <Banner tone="critical">{error}</Banner>
      </Page>
    );
  }
  if (!payload) {
    return (
      <SkeletonPage title="Shared dashboard">
        <Card>
          <SkeletonBodyText lines={8} />
        </Card>
      </SkeletonPage>
    );
  }

  const kpis = payload.widgets.filter((w) => w.widget_type === 'kpi');
  const charts = payload.widgets.filter((w) => w.widget_type !== 'kpi');

  return (
    <Page
      title={payload.name}
      subtitle={payload.description ?? undefined}
      titleMetadata={<Badge tone="info">Read-only shared view</Badge>}
    >
      <BlockStack gap="400">
        <InlineGrid columns={{ xs: 1, sm: 2, lg: 4 }} gap="300">
          {kpis.map((w) => {
            const result = liveResult(w.data);
            return (
              <Card key={w.id}>
                <BlockStack gap="100">
                  <Text as="h3" variant="headingSm" tone="subdued">
                    {w.title}
                  </Text>
                  {result ? (
                    <KpiValue
                      rows={result.rows}
                      unit={result.unit}
                      directionIsImprovement={result.direction_is_improvement}
                    />
                  ) : (
                    <Text as="p" tone="subdued">
                      —
                    </Text>
                  )}
                </BlockStack>
              </Card>
            );
          })}
        </InlineGrid>
        <InlineGrid columns={{ xs: 1, lg: 2 }} gap="300">
          {charts.map((w) => {
            const result = liveResult(w.data);
            return (
              <Card key={w.id}>
                <BlockStack gap="200">
                  <Text as="h3" variant="headingSm">
                    {w.title}
                  </Text>
                  {result ? (
                    w.widget_type === 'breakdown' ? (
                      <BarChart rows={result.rows} unit={result.unit} />
                    ) : w.widget_type === 'table' ? (
                      <MetricTable rows={result.rows} unit={result.unit} />
                    ) : (
                      <LineChart rows={result.rows} unit={result.unit} />
                    )
                  ) : (
                    <Text as="p" tone="subdued">
                      {w.data
                        ? "This widget's data is unavailable right now."
                        : 'No data for this widget yet.'}
                    </Text>
                  )}
                </BlockStack>
              </Card>
            );
          })}
        </InlineGrid>
        <Box paddingBlockEnd="400">
          <InlineStack align="center">
            <Text as="p" tone="subdued">
              Live data, resolved at load time. Powered by Markinsight.
            </Text>
          </InlineStack>
        </Box>
      </BlockStack>
    </Page>
  );
}
